/**
 * Shared helpers for immutable first-occurrence report timestamps and audit logging.
 *
 * Problem: fields like ciReportGeneratedAt are overwritten whenever a clinician
 * re-finishes a module. This breaks billing because "assessments started" counts
 * shift between months. These helpers add write-once *FirstGeneratedAt / *FirstApprovedAt
 * fields and append audit entries to a subcollection so we have a stable billing
 * anchor and a full history of every generation / approval event.
 *
 * Two Firestore update patterns exist in the codebase:
 *   1. Nested objects via updatePatientFirestore — keys like '6_manageReports'
 *   2. Dot-notation via transaction.update / updateDoc — keys like
 *      'dataModel.6_manageReports.ddReportGenerated'
 *
 * Each pattern has its own builder (buildFirst*Fields vs buildFirst*FieldsDotNotation)
 * so callers simply spread the result into their existing update objects.
 */

import { db, auth } from '../firebase';
import { collection, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { resolvePadReportStructureOutcome } from './conditionChecks';
import { emitReportAuditEvent } from '../analytics/reportEvents';

// ---------------------------------------------------------------------------
// First-occurrence timestamp builders — nested object pattern
// ---------------------------------------------------------------------------

/**
 * Returns { [prefix]ReportFirstGeneratedAt: serverTimestamp() } if the field
 * has not already been written. Otherwise returns {} so spreading it into
 * the update is a no-op. Used with updatePatientFirestore's nested-object API.
 *
 * @param {string} prefix  Report prefix, e.g. 'ci', 'ssi', 'dd', 'pad', 'pr',
 *                         'cp', 'preAR', 'qbTest', 'ratingScales', 'bupaLetter'
 * @param {Object|null|undefined} manageReports  Current value of
 *                         patientDataModel?.dataModel?.['6_manageReports']
 * @returns {Object}
 */
export function buildFirstGeneratedFields(prefix, manageReports) {
  const key = `${prefix}ReportFirstGeneratedAt`;
  if (manageReports?.[key]) return {};
  return { [key]: serverTimestamp() };
}

/**
 * Same as buildFirstGeneratedFields but for the first-approval timestamp.
 */
export function buildFirstApprovedFields(prefix, manageReports) {
  const key = `${prefix}ReportFirstApprovedAt`;
  if (manageReports?.[key]) return {};
  return { [key]: serverTimestamp() };
}

// ---------------------------------------------------------------------------
// First-occurrence timestamp builders — dot-notation pattern
// ---------------------------------------------------------------------------

/**
 * Returns { 'dataModel.6_manageReports.[prefix]ReportFirstGeneratedAt': serverTimestamp() }
 * if the field is not already set. Used with transaction.update / updateDoc.
 */
export function buildFirstGeneratedFieldsDotNotation(prefix, manageReports) {
  const shortKey = `${prefix}ReportFirstGeneratedAt`;
  if (manageReports?.[shortKey]) return {};
  return { [`dataModel.6_manageReports.${shortKey}`]: serverTimestamp() };
}

/**
 * Same as buildFirstGeneratedFieldsDotNotation but for the first-approval timestamp.
 */
export function buildFirstApprovedFieldsDotNotation(prefix, manageReports) {
  const shortKey = `${prefix}ReportFirstApprovedAt`;
  if (manageReports?.[shortKey]) return {};
  return { [`dataModel.6_manageReports.${shortKey}`]: serverTimestamp() };
}

// ---------------------------------------------------------------------------
// First-occurrence module completion builders — nested object pattern
// ---------------------------------------------------------------------------

/**
 * Returns { firstCompletedAt, firstCompletedBy, firstCompletedByName } if the
 * stage has never been completed before (i.e. stageData.firstCompletedAt is
 * falsy). Otherwise returns {} so spreading it into the update is a no-op.
 *
 * This preserves the original completion date even when a clinician re-submits
 * a module — lastCompletedAt keeps updating, but firstCompletedAt stays fixed.
 *
 * Used with updatePatientFirestore's nested-object API, e.g.:
 *   '2_clinicalInterview': {
 *     lastCompletedAt: serverTimestamp(),
 *     ...buildFirstCompletedFields(patientDataModel?.dataModel?.['2_clinicalInterview']),
 *   }
 *
 * @param {Object|null|undefined} stageData  Current value of
 *        patientDataModel?.dataModel?.['<stageKey>']
 * @returns {Object}
 */
export function buildFirstCompletedFields(stageData) {
  if (stageData?.firstCompletedAt) return {};
  const user = auth.currentUser;
  return {
    firstCompletedAt: serverTimestamp(),
    firstCompletedBy: user?.uid ?? null,
    firstCompletedByName: user?.displayName ?? null,
  };
}

// ---------------------------------------------------------------------------
// PAD module completion bundle (shared across all six PAD finish handlers)
// ---------------------------------------------------------------------------

/**
 * Builds the full `5_postAssessmentDiscussion` field bundle written when the PAD
 * module is truly completed. The PAD can be finished from any of six different
 * pages (Review, Strategies, Treatment, Health Checks, Agreed Medication Plan,
 * TOC Next Steps) depending on the outcome and org config, and each page used to
 * hand-roll this identical block — which risked drift. Centralising it here means
 * every terminal page writes exactly the same completion state.
 *
 * Crucially it stamps `padCompletedOutcome` — the positive/negative outcome the
 * PAD was actually completed under (resolved via the report-structure resolver at
 * completion time). This is the stable anchor used later by `isPadStaleVsDd` to
 * detect when a subsequently-changed Diagnostic Decision has left the completed
 * discussion out of date. Unlike the write-once `firstCompletedAt`, this is
 * refreshed on every completion so a redone PAD re-stamps the new outcome.
 *
 * @param {Object|null} patientDataModel  The full patient Firestore document
 * @param {Object} [firstCompletedFields]  Optional precomputed result of
 *        buildFirstCompletedFields(...) — pass it in when the caller also needs
 *        to know whether the first-completion timestamp was written (e.g. for the
 *        inferred-appointment-date audit) so it is only computed once.
 * @returns {Object}  Nested-object bundle to spread under '5_postAssessmentDiscussion'
 */
export function buildPadCompletionFields(patientDataModel, firstCompletedFields) {
  const stageData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion'];
  const fcf = firstCompletedFields ?? buildFirstCompletedFields(stageData);
  const user = auth.currentUser;
  return {
    status: 'completed',
    progress: 100,
    docs_available: true,
    lastCompletedAt: serverTimestamp(),
    lastCompletedBy: user?.uid ?? null,
    lastCompletedByName: user?.displayName ?? null,
    // Outcome the discussion was completed under — drives later staleness checks.
    padCompletedOutcome: resolvePadReportStructureOutcome(patientDataModel),
    ...fcf,
  };
}

// ---------------------------------------------------------------------------
// First-occurrence module completion builders — dot-notation pattern
// ---------------------------------------------------------------------------

/**
 * Returns dot-notation keys for the first module completion timestamp, e.g.:
 *   { 'dataModel.4_diagnosticDecision.firstCompletedAt': serverTimestamp(), ... }
 *
 * Only populates the fields if stageData.firstCompletedAt is not already set.
 * Used with transaction.update / updateDoc (the DD pages use this pattern).
 *
 * @param {string} stageKey  e.g. '2_clinicalInterview', '4_diagnosticDecision'
 * @param {Object|null|undefined} stageData  Current value of
 *        patientDataModel?.dataModel?.['<stageKey>']
 * @returns {Object}
 */
export function buildFirstCompletedFieldsDotNotation(stageKey, stageData) {
  if (stageData?.firstCompletedAt) return {};
  const user = auth.currentUser;
  return {
    [`dataModel.${stageKey}.firstCompletedAt`]: serverTimestamp(),
    [`dataModel.${stageKey}.firstCompletedBy`]: user?.uid ?? null,
    [`dataModel.${stageKey}.firstCompletedByName`]: user?.displayName ?? null,
  };
}

// ---------------------------------------------------------------------------
// Audit subcollection writer
// ---------------------------------------------------------------------------

/**
 * Append a single audit document to patients/{patientId}/reportAuditHistory.
 *
 * This is fire-and-forget: callers should not block on it or let failures
 * prevent the primary write from succeeding.
 *
 * @param {string} patientId  Firestore patient document ID
 * @param {Object} entry
 * @param {string} entry.module        e.g. 'ci', 'ssi', 'dd', 'pad', 'pr', 'cp', etc.
 * @param {string} entry.eventType     'generated' | 'approved' | 'approvalReset'
 * @param {boolean} entry.isFirstOccurrence  true when the first* field was just set
 */
export function appendReportAuditEntry(patientId, { module, eventType, isFirstOccurrence }) {
  const user = auth.currentUser;
  const colRef = collection(doc(db, 'patients', patientId), 'reportAuditHistory');

  addDoc(colRef, {
    module,
    eventType,
    occurredAt: serverTimestamp(),
    performedBy: user?.uid ?? null,
    performedByName: user?.displayName ?? null,
    isFirstOccurrence: Boolean(isFirstOccurrence),
  }).then(() => {
    // Analytics mirror of the entry that just landed: this one writer is the
    // choke point for every report generation and approval, so bridging here
    // covers all callers and cannot drift from the audit trail. It runs after
    // the write, carries no names or content, and never throws.
    emitReportAuditEvent(patientId, { module, eventType, isFirstOccurrence });
  }).catch((err) => {
    console.error('Failed to write report audit entry:', err);
  });
}
